import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";
import { TapTarget } from "./TapTarget";

type Variant = "primary" | "secondary" | "soft" | "ghost" | "danger" | "link";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}

// TapTarget contributes the WCAG AA floor, focus ring, cursor/select and
// disabled handling; this layer only adds variant chrome, motion, and gap.
const baseClass =
  "inline-flex items-center justify-center gap-1.5 font-medium rounded-md whitespace-nowrap " +
  "transition-[background-color,border-color,color,box-shadow,transform] duration-100 " +
  "motion-safe:active:translate-y-px";

// Hover/active states reference the ink scale, which inverts in dark mode (low ↔ high).
// In dark mode, ink-100 == surface, so ghost/secondary need an explicit `dark:` override
// to keep the hover state visible.
const variantClass: Record<Variant, string> = {
  primary: "bg-primary text-primary-fg shadow-subtle " + "hover:bg-primary/90 active:bg-primary/80",
  secondary:
    "bg-surface text-ink-900 border border-ink-200 shadow-subtle " +
    "hover:bg-ink-50 hover:border-ink-300 active:bg-ink-100 " +
    "dark:hover:bg-ink-200 dark:active:bg-ink-300",
  // Soft — subtil brand-tint, mer pa-trengende enn ghost men ikke en full
  // primary-CTA. Brukes nar handlingen er sekundaer men fortsatt skal trekkes
  // opp visuelt (eks. "Send paminnelse").
  soft:
    "bg-primary/10 text-primary " +
    "hover:bg-primary/15 active:bg-primary/20 " +
    "dark:bg-primary/20 dark:hover:bg-primary/25 dark:active:bg-primary/30",
  ghost:
    "bg-transparent text-ink-700 " +
    "hover:bg-ink-100 hover:text-ink-900 active:bg-ink-200 " +
    "dark:hover:bg-ink-200 dark:active:bg-ink-300",
  danger:
    "bg-rose-600 text-white shadow-subtle " +
    "hover:bg-rose-700 active:bg-rose-800 " +
    "dark:bg-rose-500 dark:hover:bg-rose-400 dark:active:bg-rose-300",
  link:
    "bg-transparent text-accent-600 hover:text-accent-700 " +
    "dark:text-accent-400 dark:hover:text-accent-300 " +
    "underline-offset-4 hover:underline h-auto px-0",
};

// WCAG 2.5.5: 44px tap-area pa mobil (AAA), kompakt 32-40px pa desktop.
// Sm = md i størrelse — vi vil at alle primær-CTA-er skal være like store
// (oppgjørs-, godkjenn-, last opp-knapper) for konsistent hierarki.
// Forskjellen mellom md og lg ligger i horisontal padding.
const sizeClass: Record<Size, string> = {
  sm: "h-11 sm:h-8 px-3 text-sm",
  md: "h-11 sm:h-8 px-3 text-sm",
  lg: "h-11 sm:h-10 px-4 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "secondary",
      size = "md",
      isLoading,
      iconLeft,
      iconRight,
      children,
      disabled,
      ...props
    },
    ref,
  ) => (
    <TapTarget
      ref={ref}
      className={cn(baseClass, variantClass[variant], sizeClass[size], className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? (
        <Icon name="loader" size="xs" className="animate-spin" />
      ) : (
        iconLeft && <Icon name={iconLeft} size="xs" />
      )}
      {children}
      {!isLoading && iconRight && <Icon name={iconRight} size="xs" />}
    </TapTarget>
  ),
);
Button.displayName = "Button";

/** Icon-only button — square, accessible label required. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, "iconLeft" | "iconRight" | "children"> & {
    icon: IconName;
    label: string;
  }
>(({ icon, label, size = "md", className, ...props }, ref) => {
  // WCAG 2.5.5: minst 44x44px tap-area pa mobil. Krymper til kompakt
  // ikon-storrelse fra sm-breakpoint og opp slik at desktop-layout ikke vokser.
  const sizeBox =
    size === "sm"
      ? "h-9 w-9 sm:h-7 sm:w-7"
      : size === "lg"
        ? "h-11 w-11 sm:h-10 sm:w-10"
        : "h-11 w-11 sm:h-8 sm:w-8";
  return (
    <Button
      ref={ref}
      variant={props.variant ?? "ghost"}
      size={size}
      className={cn(sizeBox, "px-0", className)}
      aria-label={label}
      title={label}
      {...props}
    >
      <Icon name={icon} size="sm" />
    </Button>
  );
});
IconButton.displayName = "IconButton";
